import { AppState as State } from '@ducks/index';
import { Contractor, Property, ReportFormat, ReportItem, SelectReportItem } from './types';
import { DisplayParams } from '@ducks/common/types';

/**
 * @summary リフレッシュを行うためのstateを返却する
 * @param {State} state
 * @returns {Record<string, never>}
 */
export const getRefreshId = (state: State) => state.report.refresh;

/**
 * @summary ローディング状態を返却する
 * @param {State} state
 * @returns {boolean} ローディング中か
 */
export const checkIsLoading = (state: State) => state.report.isLoading;

/**
 * @summary 月報フォーマットリストのローディング状態を返却する
 * @param {State} state
 * @returns {boolean} 月報フォーマットリストがローディング中か
 */
export const checkIsReportFormatListLoading = (state: State) =>
  state.report.isReportFormatListLoading;

/**
 * @summary 送信処理中かを返却する
 * @param {State} state
 * @returns {boolean} 送信処理中か
 */
export const checkIsSubmitting = (state: State) => state.report.isSubmitting;

/**
 * @summary 建物リストを返却する
 * @param {State} state
 * @returns {Property[]} 建物リスト
 */
export const getPropertyList: (state: State) => Property[] = (state: State) =>
  state.report.propertyList;

/**
 * @summary 建物リストのページャ情報を返却する
 * @param {State} state
 * @returns {DisplayParams} 建物リストのページャ情報
 */
export const getPropertyListDisplayParams: (state: State) => DisplayParams = (state: State) =>
  state.report.propertyListDisplayParams;

/**
 * @summary 契約先リストを返却する
 * @param {State} state
 * @returns {Contractor[]} 契約先リスト
 */
export const getContractorList: (state: State) => Contractor[] = (state: State) =>
  state.report.contractorList;

/**
 * @summary 契約先リストのページャ情報を返却する
 * @param {State} state
 * @returns {DisplayParams} 契約先リストのページャ情報
 */
export const getContractorListDisplayParams: (state: State) => DisplayParams = (state: State) =>
  state.report.contractorListDisplayParams;

/**
 * @summary 月報出力対象リストの建物/契約先名を返却する
 * @param {State} state
 * @returns {string} 月報出力対象リストの建物/契約先名
 */
export const getReportTargetName: (state: State) => string = (state: State) =>
  state.report.reportTargetName;

/**
 * @summary 月報の出力対象リストを返却する
 * @param {State} state
 * @returns {ReportItem[]} 月報の出力対象リスト
 */
export const getReportItemList: (state: State) => ReportItem[] = (state: State) =>
  state.report.reportItemList;

/**
 * @summary 月報の出力対象全リストを返却する
 * @param {State} state
 * @returns {ReportItem[]} 月報の出力対象リスト
 */
export const getReportItemListAll: (state: State) => ReportItem[] = (state: State) =>
  state.report.reportItemListAll;

/**
 * @summary 月報出力対象リストのページャ情報を返却する
 * @param {State} state
 * @returns {DisplayParams} 月報出力対象リストのページャ情報
 */
export const getReportItemListDisplayParams: (state: State) => DisplayParams = (state: State) =>
  state.report.reportItemDisplayParams;

/**
 * @summary 月報フォーマットリストを返却する
 * @param {State} state
 * @returns {ReportFormat[]} 月報フォーマットリスト
 */
export const getReportFormatList: (state: State) => ReportFormat[] = (state: State) =>
  state.report.reportFormatList;

/**
 * @summary 月報フォーマットリストのページャ情報を返却する
 * @param {State} state
 * @returns {DisplayParams} 月報フォーマットリストのページャ情報
 */
export const getReportFormatListDisplayParams: (state: State) => DisplayParams = (state: State) =>
  state.report.reportFormatDisplayParams;

/**
 * @summary 選択中の月報フォーマットを返却する
 * @param {State} state
 * @returns {ReportFormat | null} 選択中の月報フォーマット
 */
export const getSelectedReportFormat: (state: State) => ReportFormat | null = (state: State) =>
  state.report.selectedReportFormat;

/**
 * @summary 選択された月報出力対象リストを返却する
 * @param {State} state
 * @returns {SelectReportItem[]} 選択された月報出力対象リスト
 */
export const getSelectReportItemList: (state: State) => SelectReportItem[] = (state: State) =>
  state.report.selectReportItemList;
